import {
  useAdminDoctolibSpecialites,
  useAdminUsers,
  useSetCabinetActif,
  useSetCabinetGold,
  type AdminUser,
} from '@fidalia/api';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  cn,
  DataTable,
  Input,
  Modal,
  PageHeader,
  Pagination,
  Select,
  Switch,
  type TableColumn,
} from '@fidalia/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  MdApartment,
  MdBlock,
  MdBusiness,
  MdChevronRight,
  MdGroups,
  MdHourglassBottom,
  MdPerson,
  MdSearch,
  MdWorkspacePremium,
} from 'react-icons/md';
import { useState } from 'react';

import { errorMessage } from '@/lib/auth';
import { useDebounced } from '@/lib/use-debounced';

const PAGE_SIZE = 20;

const FONCTION_LABELS: Record<AdminUser['fonction'], string> = {
  praticien: 'Praticien',
  assistant: 'Assistant(e)',
  secretariat: 'Secrétariat',
  gestionnaire: 'Gestionnaire',
};

const STATUT_TONES: Record<AdminUser['cabinet']['statut'], 'brand' | 'success' | 'danger'> = {
  essai: 'brand',
  actif: 'success',
  impaye: 'danger',
  resilie: 'danger',
};

/** Un essai échu n'est plus un état en cours : il ne mérite plus la couleur marque. */
function toneAbonnement(
  cabinet: AdminUser['cabinet'],
): 'gold' | 'brand' | 'success' | 'danger' | 'neutral' {
  if (cabinet.gold) return 'gold';
  if (cabinet.statut === 'essai' && essaiExpire(cabinet.finEssai)) return 'neutral';
  return STATUT_TONES[cabinet.statut];
}

const NOM_FORMULE = { solo: 'Solo', cabinet: 'Cabinet', centre: 'Centre' } as const;

/** Essai dont l'échéance est passée. Sans `finEssai`, on le suppose en cours. */
function essaiExpire(finEssai: string | null): boolean {
  return finEssai !== null && new Date(finEssai).getTime() <= Date.now();
}

/** Ni Gold, ni formule, ni essai en cours : rien ne court. */
function abonnementInactif(cabinet: AdminUser['cabinet']): boolean {
  return (
    !cabinet.gold &&
    !cabinet.formule &&
    !(cabinet.statut === 'essai' && !essaiExpire(cabinet.finEssai))
  );
}

const DUREE = { mensuel: '1 mois', annuel: '12 mois' } as const;

/**
 * Ce que le cabinet paie, en une ligne. Sans formule en base, il n'y a rien
 * d'autre à montrer que l'état commercial : essai en cours, ou abonnement
 * résilié dont le Price n'a pas été conservé.
 */
function libelleAbonnement(cabinet: AdminUser['cabinet']): string {
  // Gold prime sur tout : il donne l'illimité quelle que soit la formule
  // facturée en dessous, et c'est ce qu'on cherche à repérer dans la liste.
  if (cabinet.gold) return 'Gold';

  // `statut` reste à `essai` une fois l'échéance passée — rien ne le fait
  // basculer en base. C'est la date qui tranche, pas l'enum.
  if (!cabinet.formule) return abonnementInactif(cabinet) ? 'Inactif' : 'Essai';

  const nom = NOM_FORMULE[cabinet.formule];
  const duree = cabinet.periode ? ` · ${DUREE[cabinet.periode]}` : '';

  // Un impayé ou une résiliation doit rester lisible sans compter sur la
  // couleur seule : le badge le dit après le nom de la formule.
  if (cabinet.statut === 'impaye') return `${nom}${duree} · impayé`;
  if (cabinet.statut === 'resilie') return `${nom}${duree} · résilié`;

  return `${nom}${duree}`;
}

const ICONE_FORMULE = { solo: MdPerson, cabinet: MdGroups, centre: MdApartment } as const;

/**
 * Même logique que `libelleAbonnement`, mais pour l'icône plutôt que le texte.
 * Rend l'élément directement : capturer le composant dans une variable pour
 * l'instancier ensuite (`const Icone = …; <Icone />`) construit un composant
 * pendant le rendu, ce que `react-hooks/static-components` refuse.
 */
function iconeAbonnement(cabinet: AdminUser['cabinet']) {
  const Icone = (() => {
    if (cabinet.gold) return MdWorkspacePremium;
    if (!cabinet.formule) {
      return cabinet.statut === 'essai' && !essaiExpire(cabinet.finEssai)
        ? MdHourglassBottom
        : MdBlock;
    }
    return ICONE_FORMULE[cabinet.formule];
  })();

  return <Icone className="size-3.5" aria-hidden="true" />;
}

const FILTRES_ABONNEMENT = [
  { value: '', label: 'Tous abonnements' },
  { value: 'essai', label: 'Essai en cours' },
  { value: 'expire', label: 'Essai expiré' },
  { value: 'solo', label: 'Solo' },
  { value: 'cabinet', label: 'Cabinet' },
  { value: 'centre', label: 'Centre' },
  { value: 'gold', label: 'Gold' },
  { value: 'impaye', label: 'Impayé' },
  { value: 'resilie', label: 'Résilié' },
] as const;

const JOUR_MS = 86_400_000;

/** Ancienneté d'une date, en jours : « aujourd'hui », « il y a 3 j ». */
function depuis(iso: string): string {
  const jours = Math.floor((Date.now() - new Date(iso).getTime()) / JOUR_MS);
  return jours < 1 ? 'Aujourd’hui' : `Il y a ${jours} j`;
}

/** Fin d'essai, ou fin / renouvellement de l'abonnement. Gold n'expire pas. */
function expiration(
  cabinet: AdminUser['cabinet'],
): { date: string; libelle: string; echu: boolean } | null {
  if (cabinet.gold) return null;
  const iso = cabinet.formule ? cabinet.periodeFin : cabinet.finEssai;
  if (!iso || cabinet.statut === 'resilie') return null;
  const jours = Math.ceil((new Date(iso).getTime() - Date.now()) / JOUR_MS);
  return {
    date: new Date(iso).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
    libelle:
      jours > 0
        ? `Expire dans ${jours} J`
        : jours === 0
          ? 'Expire aujourd’hui'
          : `Expiré depuis ${-jours} J`,
    echu: jours <= 0,
  };
}

type UtilisateursSearch = { sync?: 'never' | 'stale'; abonnement?: string };

export const Route = createFileRoute('/_app/users/')({
  // Filtres pré-appliqués par les liens du tableau de bord.
  validateSearch: (search: Record<string, unknown>): UtilisateursSearch => ({
    sync: search.sync === 'never' || search.sync === 'stale' ? search.sync : undefined,
    abonnement: typeof search.abonnement === 'string' ? search.abonnement : undefined,
  }),
  component: UtilisateursPage,
});

function UtilisateursPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // On n'interroge l'API qu'une fois la frappe retombée.
  const debouncedSearch = useDebounced(search, 300);
  // `undefined` = toutes spécialités confondues.
  const [specialiteId, setSpecialiteId] = useState<number | undefined>(undefined);

  /**
   * Toute nouvelle recherche repart de la première page : rester sur la page 7
   * d'un jeu de résultats qui n'en compte plus que 2 afficherait du vide.
   */
  const changeSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const initial = Route.useSearch();
  const [abonnement, setAbonnement] = useState(initial.abonnement ?? '');
  const [sync, setSync] = useState<string>(initial.sync ?? '');
  const changeSync = (value: string) => {
    setSync(value);
    setPage(1);
  };
  const changeAbonnement = (value: string) => {
    setAbonnement(value);
    setPage(1);
  };

  const changeSpecialite = (value: string) => {
    setSpecialiteId(value ? Number(value) : undefined);
    setPage(1);
  };

  const { data, isPending, error } = useAdminUsers({
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch,
    specialiteId,
    abonnement,
    sync,
  });

  // Toutes les spécialités connues, pour peupler le filtre. `limit` large :
  // c'est une taxonomie fermée, pas une liste à paginer dans un menu.
  const { data: specialites } = useAdminDoctolibSpecialites({ limit: 200 });

  const bascule = useSetCabinetActif();
  const [enCours, setEnCours] = useState<string | null>(null);
  const [aCouper, setACouper] = useState<AdminUser['cabinet'] | null>(null);
  const [erreurBascule, setErreurBascule] = useState<string | null>(null);

  const gold = useSetCabinetGold();
  const [enCoursGold, setEnCoursGold] = useState<string | null>(null);
  const [erreurGold, setErreurGold] = useState<string | null>(null);

  // Aucune conséquence à confirmer, dans un sens comme dans l'autre : contrairement
  // à Accès, activer ou retirer Gold n'interrompt rien pour le cabinet.
  const basculerGold = async (cabinetId: string, actif: boolean) => {
    setErreurGold(null);
    setEnCoursGold(cabinetId);
    try {
      await gold.mutateAsync({ cabinetId, gold: actif });
    } catch (cause) {
      setErreurGold(errorMessage(cause, 'Bascule impossible.'));
    } finally {
      setEnCoursGold(null);
    }
  };

  const appliquer = async (cabinetId: string, actif: boolean) => {
    setErreurBascule(null);
    setEnCours(cabinetId);
    try {
      await bascule.mutateAsync({ cabinetId, actif });
    } catch (cause) {
      setErreurBascule(errorMessage(cause, 'Bascule impossible.'));
    } finally {
      setEnCours(null);
    }
  };

  /**
   * Rouvrir un accès est sans conséquence, on l'applique tout de suite. Le
   * couper déconnecte immédiatement tout le cabinet, y compris des gens en
   * pleine consultation : celui-là passe par une confirmation.
   */
  const basculer = (cabinet: AdminUser['cabinet'], actif: boolean) => {
    if (actif) return void appliquer(cabinet.id, true);
    setACouper(cabinet);
  };

  const columns: TableColumn<AdminUser>[] = [
    {
      key: 'cabinet',
      title: 'Cabinet',
      icon: MdBusiness,
      cell: (user) => (
        <div className="flex items-center gap-3">
          <Avatar name={user.cabinet.nom} size="sm" tone="auto" />
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium">{user.cabinet.nom}</span>
            <span className="truncate text-xs text-content-muted">{user.email}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'personne',
      title: 'Responsable',
      icon: MdPerson,
      cell: (user) => {
        const nom = `${user.prenom} ${user.nom}`.trim();
        return (
          <div className="flex items-center gap-3">
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{nom}</span>
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate text-xs text-content-muted">
                  {FONCTION_LABELS[user.fonction]}
                </span>
                {user.accesSecondaires > 0 ? (
                  <Badge
                    tone="neutral"
                    size="sm"
                    className="px-1.5 py-0.5"
                    title={`${user.accesSecondaires} accès secondaire${
                      user.accesSecondaires > 1 ? 's' : ''
                    } dans ce cabinet`}
                  >
                    +{user.accesSecondaires}
                  </Badge>
                ) : null}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      key: 'abonnement',
      title: 'Abonnement',
      // Le nom de la formule, pas le statut : « actif » ne dit pas ce qui est
      // vendu. Le statut survit dans la couleur du badge, et en libellé quand
      // il n'y a pas encore de formule (essai) ou qu'elle est suspendue.
      cell: (user) => (
        <Badge tone={toneAbonnement(user.cabinet)} icon={iconeAbonnement(user.cabinet)}>
          {libelleAbonnement(user.cabinet)}
        </Badge>
      ),
    },
    {
      key: 'expiration',
      title: 'Expiration',
      cell: (user) => {
        // Gold n'expire jamais : accès illimité, sans facturation.
        if (user.cabinet.gold) return <span className="text-content-muted">Jamais</span>;
        const exp = expiration(user.cabinet);
        if (!exp) return <span className="text-content-subtle">—</span>;
        return (
          <div className="flex flex-col">
            <span className={cn('font-medium', exp.echu ? 'text-danger' : 'text-content')}>
              {exp.libelle}
            </span>
            <span className="text-xs text-content-muted">{exp.date}</span>
          </div>
        );
      },
    },
    {
      key: 'extension',
      title: 'Sync. Doctolib',
      cell: (user) =>
        user.syncAt ? (
          <span className="text-content-muted">{depuis(user.syncAt)}</span>
        ) : (
          <span className="text-content-subtle">—</span>
        ),
    },
    {
      key: 'gold',
      title: 'Gold',
      cell: (user) => (
        // `stopPropagation` : même raison que le switch Accès juste après —
        // la ligne entière navigue vers la fiche.
        <span onClick={(event) => event.stopPropagation()}>
          <Switch
            label={`Formule Gold du cabinet ${user.cabinet.nom}`}
            hideLabel
            checked={user.cabinet.gold}
            loading={gold.isPending && enCoursGold === user.cabinet.id}
            onChange={(actif) => basculerGold(user.cabinet.id, actif)}
            tone="gold"
            title={
              user.cabinet.gold
                ? 'Retirer l’accès illimité de ce cabinet'
                : 'Accorder un accès illimité à ce cabinet, sans facturation'
            }
          />
        </span>
      ),
    },
    {
      key: 'acces',
      title: 'Accès',
      cell: (user) => (
        // `stopPropagation` : la ligne entière ouvre la fiche, un clic sur
        // l'interrupteur ne doit pas naviguer en même temps qu'il bascule.
        <span onClick={(event) => event.stopPropagation()}>
          <Switch
            label={`Accès du cabinet ${user.cabinet.nom}`}
            hideLabel
            checked={user.cabinet.actif}
            loading={bascule.isPending && enCours === user.cabinet.id}
            onChange={(actif) => basculer(user.cabinet, actif)}
            title={
              user.cabinet.actif
                ? 'Couper l’accès de tous les comptes de ce cabinet'
                : 'Rouvrir l’accès de ce cabinet'
            }
          />
        </span>
      ),
    },
    {
      key: 'chevron',
      hideOnMobile: true,
      align: 'right',
      className: 'w-px',
      cell: () => <MdChevronRight aria-hidden="true" className="size-5 text-content-subtle" />,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<MdGroups />}
        title="Utilisateurs"
        description="Un cabinet par ligne, représenté par son compte responsable."
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Input
          type="search"
          aria-label="Rechercher un utilisateur"
          placeholder="Nom, email, cabinet…"
          icon={<MdSearch className="size-6" />}
          containerClassName="h-11 w-full sm:min-w-64 sm:flex-1"
          value={search}
          onChange={(event) => changeSearch(event.target.value)}
        />

        <Select
          aria-label="Filtrer par abonnement"
          containerClassName="h-11 w-full sm:w-48"
          value={abonnement}
          onChange={(event) => changeAbonnement(event.target.value)}
        >
          {FILTRES_ABONNEMENT.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Filtrer par extension"
          containerClassName="h-11 w-full sm:w-52"
          value={sync}
          onChange={(event) => changeSync(event.target.value)}
        >
          <option value="">Toutes extensions</option>
          <option value="never">Jamais synchronisée</option>
          <option value="stale">Synchro ancienne (7 j+)</option>
        </Select>

        <Select
          aria-label="Filtrer par spécialité"
          containerClassName="h-11 w-full sm:w-56"
          value={specialiteId !== undefined ? String(specialiteId) : ''}
          onChange={(event) => changeSpecialite(event.target.value)}
        >
          <option value="">Toutes spécialités</option>
          {(specialites?.items ?? []).map((specialite) => (
            <option key={specialite.id} value={specialite.id}>
              {specialite.nom}
            </option>
          ))}
        </Select>
      </div>

      {error ? <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert> : null}
      {erreurBascule ? <Alert tone="danger">{erreurBascule}</Alert> : null}
      {erreurGold ? <Alert tone="danger">{erreurGold}</Alert> : null}

      <Modal
        open={aCouper !== null}
        onClose={() => setACouper(null)}
        size="md"
        title="Couper l’accès de ce cabinet ?"
        description={
          <>
            Tous les comptes de <strong>{aCouper?.nom}</strong> seront déconnectés immédiatement et
            ne pourront plus se connecter. L’abonnement Stripe, lui, continue de courir : cette
            bascule ne résilie rien et ne rembourse rien.
          </>
        }
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setACouper(null)}>
              Annuler
            </Button>
            <Button
              variant="danger"
              loading={bascule.isPending}
              onClick={() => {
                const cible = aCouper;
                setACouper(null);
                if (cible) void appliquer(cible.id, false);
              }}
            >
              Couper l’accès
            </Button>
          </div>
        }
      />

      <DataTable
        mobileCards
        data={data?.items ?? []}
        columns={columns}
        isLoading={isPending}
        skeletonRows={10}
        rowId={(user) => user.id}
        onRowClick={(user) => void navigate({ to: '/users/$id', params: { id: user.id } })}
        emptyMessage={
          debouncedSearch ? `Aucun résultat pour « ${debouncedSearch} ».` : 'Aucun utilisateur.'
        }
      />

      {data ? (
        <Pagination
          page={data.page}
          total={data.total}
          limit={data.limit}
          onChange={setPage}
          label="cabinets"
        />
      ) : null}
    </div>
  );
}
